import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, CogIcon, UserIcon, PencilSquareIcon, ArrowRightOnRectangleIcon, InformationCircleIcon, AddressBookIcon, PhotoIcon, ClockIcon } from '../UI/Icons';
import ProfileSettingsPanel from './ProfileSettingsPanel';
import BusinessHoursPanel from './BusinessHoursPanel';
import { useAuth } from '../../contexts/AuthContext';
import { useActualApi } from '../../hooks/useActualApi';
import { useSettings } from '../../contexts/SettingsContext';
import { useAddonManager } from '../Addons/hooks/useAddonManager';
import { API_BASE_URL } from '../../constants';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose }) => {
  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState(false);
  const [isBusinessHoursPanelOpen, setIsBusinessHoursPanelOpen] = useState(false);
  const [globalBotPaused, setGlobalBotPaused] = useState(false);
  const [isUpdatingGlobalPause, setIsUpdatingGlobalPause] = useState(false);
  const [isLoadingGlobalPause, setIsLoadingGlobalPause] = useState(false);
  const [isUpdatingProfilePictures, setIsUpdatingProfilePictures] = useState(false);
  const [isUpdatingTriggerNotifications, setIsUpdatingTriggerNotifications] = useState(false);
  const [enableImageVision, setEnableImageVision] = useState(false);
  const [isUpdatingImageVision, setIsUpdatingImageVision] = useState(false);
  const [enableVoiceNotes, setEnableVoiceNotes] = useState(false);
  const [isUpdatingVoiceNotes, setIsUpdatingVoiceNotes] = useState(false);
  const [isLoadingChatbotSettings, setIsLoadingChatbotSettings] = useState(false);
  
  const { currentUser, logout, isLoading: isLoggingOut } = useAuth();
  const { toggleGlobalBotPause, getClientChats } = useActualApi();
  const { settings, updateSetting, error: settingsError } = useSettings();
  const { getAddon } = useAddonManager();
  const navigate = useNavigate();

  // Check if WhatsApp addon is enabled
  const whatsappAddon = getAddon('whatsapp');
  const isWhatsAppEnabled = whatsappAddon?.isEnabled && whatsappAddon?.setupComplete;

  // Fetch global bot pause state when panel opens
  useEffect(() => {
    const fetchGlobalBotPause = async () => {
      if (isOpen) {
        setIsLoadingGlobalPause(true);
        try {
          const response = await getClientChats();
          setGlobalBotPaused(response.globalBotPaused || false);
        } catch (error) {
          console.error('Error fetching global bot pause state:', error);
        } finally {
          setIsLoadingGlobalPause(false);
        }
      }
    };
    fetchGlobalBotPause();
  }, [isOpen, getClientChats]);

  // Fetch chatbot settings (including image vision) when panel opens
  useEffect(() => {
    const fetchChatbotSettings = async () => {
      if (isOpen && isWhatsAppEnabled) {
        setIsLoadingChatbotSettings(true);
        try {
          const idToken = await currentUser?.getIdToken();
          if (!idToken) return;

          const response = await fetch(`${API_BASE_URL}/client/chatbot_settings`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.settings) {
              setEnableImageVision(data.settings.enableImageVision || false);
              setEnableVoiceNotes(data.settings.enableVoiceNotes || false);
            }
          }
        } catch (error) {
          console.error('Error fetching chatbot settings:', error);
        } finally {
          setIsLoadingChatbotSettings(false);
        }
      }
    };
    fetchChatbotSettings();
  }, [isOpen, isWhatsAppEnabled, currentUser]);

  const handleEditBusinessHours = () => {
    setIsBusinessHoursPanelOpen(true);
  };

  const closeBusinessHoursPanel = () => {
    setIsBusinessHoursPanelOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
      // Force a hard reload to clear all state and ensure clean login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Even on error, try to go to login
      window.location.href = '/login';
    }
  };

  const handleToggleGlobalBotPause = async () => {
    try {
      setIsUpdatingGlobalPause(true);
      const newPauseState = !globalBotPaused;
      await toggleGlobalBotPause({ pause: newPauseState });
      setGlobalBotPaused(newPauseState);
    } catch (error) {
      console.error('Error toggling global bot pause:', error);
      // Reset to previous state on error
      alert('Failed to update global bot pause. Please try again.');
    } finally {
      setIsUpdatingGlobalPause(false);
    }
  };

  const handleToggleProfilePictures = async () => {
    try {
      setIsUpdatingProfilePictures(true);
      const newValue = !settings.showRandomProfilePictures;
      await updateSetting('showRandomProfilePictures', newValue);
    } catch (error) {
      console.error('Error updating profile pictures setting:', error);
      // The setting will show the optimistic update, but we could show an error toast here
    } finally {
      setIsUpdatingProfilePictures(false);
    }
  };

  const handleToggleTriggerNotifications = async () => {
    try {
      setIsUpdatingTriggerNotifications(true);
      const newValue = !settings.showTriggerNotifications;
      await updateSetting('showTriggerNotifications', newValue);
    } catch (error) {
      console.error('Error updating trigger notifications setting:', error);
      // The setting will show the optimistic update, but we could show an error toast here
    } finally {
      setIsUpdatingTriggerNotifications(false);
    }
  };

  const handleToggleImageVision = async () => {
    try {
      setIsUpdatingImageVision(true);
      const newValue = !enableImageVision;
      
      const idToken = await currentUser?.getIdToken();
      if (!idToken) {
        alert('Authentication error. Please try again.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/client/chatbot_settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enableImageVision: newValue }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEnableImageVision(newValue);
        } else {
          alert('Failed to update AI Image Vision setting.');
        }
      } else {
        alert('Failed to update AI Image Vision setting.');
      }
    } catch (error) {
      console.error('Error updating image vision setting:', error);
      alert('Failed to update AI Image Vision setting.');
    } finally {
      setIsUpdatingImageVision(false);
    }
  };

  const handleToggleVoiceNotes = async () => {
    try {
      setIsUpdatingVoiceNotes(true);
      const newValue = !enableVoiceNotes;

      const idToken = await currentUser?.getIdToken();
      if (!idToken) {
        alert('Authentication error. Please try again.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/client/chatbot_settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enableVoiceNotes: newValue }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEnableVoiceNotes(newValue);
        } else {
          alert('Failed to update AI Voice Notes setting.');
        }
      } else {
        alert('Failed to update AI Voice Notes setting.');
      }
    } catch (error) {
      console.error('Error updating voice notes setting:', error);
      alert('Failed to update AI Voice Notes setting.');
    } finally {
      setIsUpdatingVoiceNotes(false);
    }
  };

  const handleEditProfile = () => {
    setIsProfilePanelOpen(true);
  };

  const closeProfilePanel = () => {
    setIsProfilePanelOpen(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ease-in-out"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Centered Modal Panel */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-panel-title"
      >
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] transform transition-all duration-300 ease-in-out scale-100 opacity-100">
          <div className="flex flex-col h-full max-h-[90vh]">
            {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <CogIcon className="w-6 h-6 text-gray-600" />
              <h2 id="settings-panel-title" className="text-lg font-semibold text-gray-800">
                Settings
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              aria-label="Close settings"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-grow p-6 overflow-y-auto">
            {/* User Information Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">User Information</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-3">
                  {currentUser?.photoURL ? (
                    <img
                      src={currentUser.photoURL}
                      alt="Profile"
                      className="w-12 h-12 rounded-full object-cover border border-gray-300"
                    />
                  ) : (
                    <UserIcon className="w-12 h-12 text-gray-400 bg-gray-200 rounded-full p-2" />
                  )}
                  <div className="flex-grow">
                    <p className="text-sm font-medium text-gray-900">
                      {currentUser?.displayName || currentUser?.email || 'User'}
                    </p>
                    <p className="text-sm text-gray-500">{currentUser?.email}</p>
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  Account created: {currentUser?.metadata?.creationTime ?
                    new Date(currentUser.metadata.creationTime).toLocaleDateString() : 'Unknown'}
                </div>
              </div>
            </div>

            {/* Settings Error Display */}
            {settingsError && (
              <div className="mb-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <XMarkIcon className="w-5 h-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">Settings Error</p>
                      <p className="text-sm text-red-700">{settingsError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Global Bot Control Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Global Bot Control</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <InformationCircleIcon className="w-5 h-5 text-blue-500" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Global Bot Pause</h4>
                      <p className="text-sm text-gray-500">
                        {isLoadingGlobalPause
                          ? "Loading..."
                          : globalBotPaused
                            ? "Bot is globally paused for all numbers"
                            : "Bot is active for all numbers (unless individually paused)"
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={handleToggleGlobalBotPause}
                      disabled={isUpdatingGlobalPause || isLoadingGlobalPause}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                        globalBotPaused ? 'bg-red-500' : 'bg-green-500'
                      } ${(isUpdatingGlobalPause || isLoadingGlobalPause) ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          globalBotPaused ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    {isUpdatingGlobalPause && (
                      <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* UI Preferences Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">UI Preferences</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <UserIcon className="w-5 h-5 text-purple-500" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Random Chat Profile Pictures</h4>
                      <p className="text-sm text-gray-500">
                        {settings.showRandomProfilePictures
                          ? "Show random profile pictures for chat contacts"
                          : "Show initials instead of random profile pictures"
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={handleToggleProfilePictures}
                      disabled={isUpdatingProfilePictures}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                        settings.showRandomProfilePictures ? 'bg-green-500' : 'bg-gray-400'
                      } ${isUpdatingProfilePictures ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          settings.showRandomProfilePictures ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    {isUpdatingProfilePictures && (
                      <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">AI Trigger Notifications</h4>
                      <p className="text-sm text-gray-500">
                        {settings.showTriggerNotifications
                          ? "Show notification badge when AI triggers activate"
                          : "Hide AI trigger notification badges"
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={handleToggleTriggerNotifications}
                      disabled={isUpdatingTriggerNotifications}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                        settings.showTriggerNotifications ? 'bg-purple-500' : 'bg-gray-400'
                      } ${isUpdatingTriggerNotifications ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          settings.showTriggerNotifications ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    {isUpdatingTriggerNotifications && (
                      <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp AI Settings Section - Only show if WhatsApp addon is enabled */}
            {isWhatsAppEnabled && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">WhatsApp AI Settings</h3>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">AI Image Vision</h4>
                        <p className="text-sm text-gray-600">
                          {isLoadingChatbotSettings
                            ? "Loading..."
                            : enableImageVision
                              ? "Bot can see and analyze images sent by customers"
                              : "Bot cannot analyze images (text-only responses)"
                          }
                        </p>
                        {enableImageVision && (
                          <p className="text-xs text-green-700 mt-1">
                            ✨ Powered by Gemini 2.0 Flash multimodal AI
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <button
                        onClick={handleToggleImageVision}
                        disabled={isUpdatingImageVision || isLoadingChatbotSettings}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                          enableImageVision ? 'bg-green-600' : 'bg-gray-400'
                        } ${(isUpdatingImageVision || isLoadingChatbotSettings) ? 'opacity-50' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                            enableImageVision ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      {isUpdatingImageVision && (
                        <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 mt-4 border-t border-green-200">
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 10v2a7 7 0 11-14 0v-2" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19v4" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 23h8" />
                      </svg>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">AI Voice Notes</h4>
                        <p className="text-sm text-gray-600">
                          {isLoadingChatbotSettings
                            ? "Loading..."
                            : enableVoiceNotes
                              ? "Bot can understand and respond to customer voice notes"
                              : "Bot cannot process voice notes"
                          }
                        </p>
                        {enableVoiceNotes && (
                          <p className="text-xs text-green-700 mt-1">
                            ✨ Powered by Gemini 2.0 Flash multimodal AI
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <button
                        onClick={handleToggleVoiceNotes}
                        disabled={isUpdatingVoiceNotes || isLoadingChatbotSettings}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                          enableVoiceNotes ? 'bg-green-600' : 'bg-gray-400'
                        } ${(isUpdatingVoiceNotes || isLoadingChatbotSettings) ? 'opacity-50' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                            enableVoiceNotes ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      {isUpdatingVoiceNotes && (
                        <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Options */}
            <div className="space-y-4">
              <button
                onClick={handleEditProfile}
                className="w-full flex items-center p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors duration-200"
              >
                <PencilSquareIcon className="w-6 h-6 text-gray-600 mr-3 flex-shrink-0" />
                <div className="flex-grow">
                  <h3 className="text-sm font-medium text-gray-900">Edit Profile</h3>
                  <p className="text-sm text-gray-500">Manage your profile information</p>
                </div>
              </button>

              {/* Business Hours Button */}
              <button
                onClick={handleEditBusinessHours}
                className="w-full flex items-center p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors duration-200"
              >
                <ClockIcon className="w-6 h-6 text-gray-600 mr-3 flex-shrink-0" />
                <div className="flex-grow">
                  <h3 className="text-sm font-medium text-gray-900">Business Hours</h3>
                  <p className="text-sm text-gray-500">Configure operating hours</p>
                </div>
              </button>

              {/* Refresh moved to chat-list settings dropdown */}

              {/* Media Library Button */}
              <button
                onClick={() => { navigate('/media'); onClose(); }}
                className="w-full flex items-center p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors duration-200"
              >
                <PhotoIcon className="w-6 h-6 text-gray-600 mr-3 flex-shrink-0" />
                <div className="flex-grow">
                  <h3 className="text-sm font-medium text-gray-900">Media Library</h3>
                  <p className="text-sm text-gray-500">Manage uploaded media files</p>
                </div>
              </button>

              {/* Team Management Button */}
              <button
                onClick={() => { navigate('/team'); onClose(); }}
                className="w-full flex items-center p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors duration-200"
              >
                <UserIcon className="w-6 h-6 text-gray-600 mr-3 flex-shrink-0" />
                <div className="flex-grow">
                  <h3 className="text-sm font-medium text-gray-900">Team Management</h3>
                  <p className="text-sm text-gray-500">Manage team members for handoff notifications</p>
                </div>
              </button>

              {/* Contacts Button */}
              <button
                onClick={() => { navigate('/phonebook'); onClose(); }}
                className="w-full flex items-center p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors duration-200"
              >
                <AddressBookIcon className="w-6 h-6 text-gray-600 mr-3 flex-shrink-0" />
                <div className="flex-grow">
                  <h3 className="text-sm font-medium text-gray-900">Contacts</h3>
                  <p className="text-sm text-gray-500">Manage your contact phonebook</p>
                </div>
              </button>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="w-full flex items-center p-4 text-left border border-red-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowRightOnRectangleIcon className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" />
                <div className="flex-grow">
                  <h3 className="text-sm font-medium text-red-900">Log Out</h3>
                  <p className="text-sm text-red-600">Sign out of your account</p>
                </div>
                {isLoggingOut && (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <p className="text-xs text-gray-500 text-center">
              WAPP Control Dashboard v1.0
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Profile Settings Panel */}
    <ProfileSettingsPanel
      isOpen={isProfilePanelOpen}
      onClose={closeProfilePanel}
    />
    
    {/* Business Hours Panel */}
    <BusinessHoursPanel
        isOpen={isBusinessHoursPanelOpen}
        onClose={closeBusinessHoursPanel}
    />
  </>
);
};

export default SettingsPanel;